"use client";

import { useMemo, useState, useEffect } from "react";
import ReactMarkdown from "react-markdown";

/* ---------------- TYPES ---------------- */

type MediaField = {
    media?: {
        file?: string;
        alt?: string;
    };
};

type NewsPost = {
    title?: string;
    slug?: string;
    author?: string;
    publishedDate?: string;
    category?: string;
    featuredImage?: MediaField;
    excerpt?: string;
    content?: string;
};

type Props = {
    data: {
        posts?: NewsPost[];
    };
};

const PAGE_SIZE = 10;

/* ================= COMPONENT ================= */

export default function ProWhipNewsPost({ data }: Props) {

    const posts: NewsPost[] = Array.isArray(data?.posts) ? data.posts : [];

    const [searchValue, setSearchValue] = useState("");
    const [currentPage, setCurrentPage] = useState(1);
    const [selectedPost, setSelectedPost] = useState<NewsPost | null>(null);

    /* ---------------- LOCK BODY SCROLL ---------------- */

    useEffect(() => {
        if (selectedPost) {
            document.body.style.overflow = "hidden";
        } else {
            document.body.style.overflow = "";
        }

        return () => {
            document.body.style.overflow = "";
        };
    }, [selectedPost]);

    /* ---------------- FILTER + SORT ---------------- */

    const filteredPosts = useMemo(() => {
        let result = [...posts];

        if (searchValue.trim()) {
            result = result.filter((p) =>
                p.title?.toLowerCase().includes(searchValue.toLowerCase())
            );
        }

        result.sort((a, b) => {
            const d1 = new Date(b.publishedDate || "").getTime();
            const d2 = new Date(a.publishedDate || "").getTime();
            return d1 - d2;
        });

        return result;
    }, [posts, searchValue]);

    /* ---------------- PAGINATION ---------------- */

    const totalPages = Math.max(1, Math.ceil(filteredPosts.length / PAGE_SIZE));

    const pageItems = filteredPosts.slice(
        (currentPage - 1) * PAGE_SIZE,
        currentPage * PAGE_SIZE
    );

    /* ---------------- HELPERS ---------------- */

    const formatDate = (date?: string) =>
        date
            ? new Date(date).toLocaleString("en-GB", {
                year: "numeric",
                month: "2-digit",
                day: "2-digit",
                hour: "2-digit",
                minute: "2-digit",
            })
            : "-";

    /* ================= RENDER ================= */

    return (
        <section className="w-full bg-white">

            <div className="max-w-[1200px] mx-auto mt-12 sm:mt-16 px-4 sm:px-6 py-8 sm:py-12">

                {/* TITLE */}

                <h1 className="text-2xl sm:text-3xl font-semibold mb-6 sm:mb-8">
                    Notice
                </h1>

                {/* SEARCH */}

                <div className="flex flex-col sm:flex-row sm:justify-end gap-2 mb-6">

                    <select className="border px-3 py-2 text-sm">
                        <option>Title</option>
                    </select>

                    <input
                        value={searchValue}
                        onChange={(e) => {
                            setSearchValue(e.target.value);
                            setCurrentPage(1);
                        }}
                        placeholder="Search"
                        className="border px-3 py-2 w-full sm:w-64 text-sm"
                    />

                    <button className="border px-4 py-2 text-sm bg-gray-100 hover:bg-gray-200">
                        Search
                    </button>

                </div>

                {/* TABLE HEADER */}

                <div className="hidden md:grid grid-cols-[1fr_180px_180px_100px] border-b py-3 font-medium text-sm">
                    <div>Title</div>
                    <div>Publisher</div>
                    <div>Date</div>
                    <div>Filter</div>
                </div>

                {/* EMPTY STATE */}

                {pageItems.length === 0 && (
                    <div className="py-10 text-center text-gray-500">
                        No posts found.
                    </div>
                )}

                {/* LIST */}

                {pageItems.map((post, idx) => (
                    <div
                        key={idx}
                        className="grid md:grid-cols-[1fr_180px_180px_100px] grid-cols-1 gap-2 md:gap-0 border-b py-4 text-sm"
                    >

                        {/* TITLE */}

                        <button
                            onClick={() => setSelectedPost(post)}
                            className="text-left hover:underline font-medium md:font-normal"
                        >
                            {post.title}
                        </button>

                        {/* AUTHOR */}

                        <div className="text-gray-600 md:text-black text-xs md:text-sm">
                            <span className="md:hidden font-medium">Publisher: </span>
                            {post.author || "-"}
                        </div>

                        {/* DATE */}

                        <div className="text-gray-600 md:text-black text-xs md:text-sm">
                            <span className="md:hidden font-medium">Date: </span>
                            {formatDate(post.publishedDate)}
                        </div>

                        {/* FILTER */}

                        <div className="hidden md:block">-</div>

                    </div>
                ))}

                {/* PAGINATION */}

                <div className="flex flex-col sm:flex-row gap-4 sm:gap-0 justify-between items-center mt-8 text-sm">

                    <div />

                    <div className="flex gap-2 flex-wrap justify-center">

                        {Array.from({ length: totalPages }).map((_, i) => (
                            <button
                                key={i}
                                onClick={() => setCurrentPage(i + 1)}
                                className={`px-3 py-2 rounded ${currentPage === i + 1
                                        ? "bg-gray-300"
                                        : "bg-gray-100 hover:bg-gray-200"
                                    }`}
                            >
                                {i + 1}
                            </button>
                        ))}

                    </div>

                    <div>
                        Page: {currentPage} / {totalPages}
                    </div>

                </div>

            </div>

            {/* ================= POPUP DETAIL ================= */}

            {selectedPost && (

                <div
                    className="fixed inset-0 z-[9999] bg-black/80 backdrop-blur-sm flex justify-center items-start overflow-y-auto"
                    onClick={() => setSelectedPost(null)}
                >

                    <div
                        className="relative bg-white max-w-[900px] w-full mt-[80px] sm:mt-[120px] mb-12 p-5 sm:p-8 rounded shadow-2xl"
                        onClick={(e) => e.stopPropagation()}
                    >

                        {/* CLOSE */}

                        <button
                            onClick={() => setSelectedPost(null)}
                            className="absolute top-4 right-4 text-xl hover:opacity-70"
                        >
                            ✕
                        </button>

                        {/* TITLE */}

                        <h2 className="text-xl sm:text-2xl font-semibold mb-4">
                            {selectedPost.title}
                        </h2>

                        {/* META */}

                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-y-2 text-sm border-t border-b py-4 mb-6 sm:mb-8">

                            <div className="font-medium">Publisher</div>
                            <div>{selectedPost.author || "-"}</div>

                            <div className="font-medium">Date</div>
                            <div>{formatDate(selectedPost.publishedDate)}</div>

                        </div>

                        {/* IMAGE */}

                        {selectedPost.featuredImage?.media?.file && (
                            <img
                                src={selectedPost.featuredImage.media.file}
                                alt={selectedPost.featuredImage.media.alt || ""}
                                className="w-full mb-6 sm:mb-8"
                            />
                        )}

                        {/* CONTENT */}

                        {selectedPost.content && (
                            <div className="prose max-w-none">
                                <ReactMarkdown>
                                    {selectedPost.content}
                                </ReactMarkdown>
                            </div>
                        )}

                    </div>

                </div>

            )}

        </section>
    );
}
